import { motion } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { ADDRESSES, mapsUrl } from "./Contact";
import { SectionLabel } from "./Motion";
import { saveConsent, useConsent } from "@/lib/consent";
import { useT } from "@/i18n";

const embed = (q) => `https://www.google.com/maps?q=${encodeURIComponent(q)}&z=15&output=embed`;

const MapConsent = ({ t, consent }) => (
  <div className="grid h-full w-full place-items-center bg-[#1B1816] p-6 text-center" data-testid="map-consent">
    <div>
      <MapPin className="mx-auto h-8 w-8 text-[#D98A52]" />
      <p className="mx-auto mt-4 max-w-xs text-sm text-[#D6D0C4]">{t("ck.mapBlocked", "La mappa è di Google e può usare cookie: si mostra solo con il tuo consenso.")}</p>
      <button onClick={() => saveConsent({ stats: !!consent?.stats, maps: true })} className="btn-amber mt-5" data-testid="map-consent-accept">{t("ck.mapShow", "Mostra le mappe")}</button>
    </div>
  </div>
);

const HOURS = [
  [["Amministrazione", ["09:00", "14:00"], ["15:30", "18:00"]]],
  [["Stabilimento", ["07:00", "13:00"], ["14:30", "17:00"]], ["Carico e scarico merci", ["07:30", "12:00"], ["14:30", "16:00"]]],
];

const toMin = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };

const isOpen = (slots) => {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Rome", weekday: "short", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t)?.value ?? "";
  if (["Sat", "Sun"].includes(get("weekday"))) return false;
  const now = toMin(`${get("hour")}:${get("minute")}`);
  return slots.some(([a, b]) => now >= toMin(a) && now < toMin(b));
};

const Hours = ({ rows, idx }) => {
  const t = useT();
  return (
  <div className="border-t border-white/10 px-6 py-6 sm:px-8" data-testid={`location-hours-${idx}`}>
    <p className="mb-4 font-mono text-xs uppercase tracking-[0.2em] text-[#A39E93]">{t("loc.hours", "Orari · lunedì–venerdì")}</p>
    <dl className="space-y-3">
      {rows.map(([label, am, pm]) => {
        const open = isOpen([am, pm]);
        return (
          <div key={label} className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1">
            <dt className="flex items-center gap-3 font-display text-base font-semibold">
              <span className={`h-2 w-2 rounded-full ${open ? "bg-[#7DB33A]" : "bg-[#A39E93]/50"}`} />
              {t(`loc.${label}`, label)}
              <span className={`font-mono text-xs uppercase tracking-[0.15em] ${open ? "text-[#9BD13F]" : "text-[#A39E93]"}`}>{open ? t("loc.open", "Aperto ora") : t("loc.closed", "Chiuso ora")}</span>
            </dt>
            <dd className="font-mono text-sm text-[#F7F5F0]">{am.join("–")} · {pm.join("–")}</dd>
          </div>
        );
      })}
    </dl>
  </div>
  );
};

export const Locations = () => {
  const t = useT();
  const consent = useConsent();
  return (
  <section id="sedi" className="bg-[#121110] px-5 pb-28 text-[#F7F5F0] sm:px-8 lg:pb-40" data-testid="locations-section">
    <div className="mx-auto max-w-[1500px] border-t border-white/10 pt-16">
      <SectionLabel index="10" dark>{t("loc.label", "Dove siamo")}</SectionLabel>
      <div className="mt-10 grid gap-4 md:grid-cols-2">
        {ADDRESSES.map(([k, a, c], i) => {
          const q = `${a}, ${c}`;
          return (
            <motion.article
              key={k}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.9, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="group overflow-hidden border border-white/10 bg-[#1B1816]"
              data-testid={`location-card-${i}`}
            >
              <div className="relative h-[320px] overflow-hidden sm:h-[380px]">
                {consent?.maps ? <iframe
                  title={`Mappa ${k}`}
                  src={embed(q)}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="h-full w-full border-0 grayscale-[85%] invert-[0.9] hue-rotate-180 transition-[filter] duration-700 group-hover:grayscale-0 group-hover:invert-0 group-hover:hue-rotate-0"
                  data-testid={`location-map-${i}`}
                /> : <MapConsent t={t} consent={consent} />}
              </div>
              <div className="flex flex-wrap items-end justify-between gap-6 p-6 sm:p-8">
                <div className="flex gap-4">
                  <MapPin className="mt-1 h-5 w-5 shrink-0 text-[#D98A52]" />
                  <div>
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D98A52]">{t(`contact.k.${k}`, k)}</p>
                    <p className="mt-2 font-display text-xl font-semibold tracking-tight">{a}</p>
                    <p className="text-[#A39E93]">{c}</p>
                  </div>
                </div>
                <a href={mapsUrl(`Emma Industrie ${q}`)} target="_blank" rel="noreferrer" className="btn-amber" data-testid={`location-open-${i}`}>
                  {t("loc.open_maps", "Apri in Google Maps")} <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
              <Hours rows={HOURS[i] ?? []} idx={i} />
            </motion.article>
          );
        })}
      </div>
    </div>
  </section>
  );
};
